import { useEffect, useLayoutEffect } from 'react';

import sanitizeRestaurantThemeColor from 'utils/sanitize-color';


const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Sets the --restaurant-color CSS custom property at the document level while the component is mounted.
 * Falls back to removing the inline override so global defaults take over once the page unmounts.
 * The public restaurant menu calls this so it can adopt the tenant's accent colour while admin screens keep defaults.
 */
const useRestaurantThemeColor = (color) => {
  const sanitizedColor = sanitizeRestaurantThemeColor(color);

  useIsomorphicLayoutEffect(() => {
    // React hook effects conventionally return undefined (or nothing) when no cleanup is needed, or a cleanup function when cleanup is required.
    // eslint-disable-next-line no-undefined
    if (!sanitizedColor || typeof document === 'undefined') return undefined;

    const root = document.documentElement;
    // Capture any inline override applied before the menu mounts.
    const previousColor = root.style.getPropertyValue('--restaurant-color');
    const hadInlineOverride = previousColor !== '';

    root.style.setProperty('--restaurant-color', sanitizedColor);

    return () => {
      if (hadInlineOverride) {
        root.style.setProperty('--restaurant-color', previousColor);
      } else {
        root.style.removeProperty('--restaurant-color');
      }
    };
  }, [sanitizedColor]);
};

export default useRestaurantThemeColor;
